import { useState, useCallback } from 'react';
import cs from 'classnames';
import TruncateMarkup from 'react-truncate-markup';
import { format, parseISO } from 'date-fns';
import { Video, ArticleVariant } from '@repo/types';
import { formatTag } from '../../../utils/formatTag';
import { Image } from '../../../common/components/Image';
import { VideoOverlay } from '../VideoOverlay/VideoOverlay';
import { trackVideo } from '../../../utils/tracking';

const VIDEO_DATE_FORMAT = 'dd MMM yyyy';

type VideoCardBaseProps = {
  showTag?: boolean;
  showDate?: boolean;
  variant?: ArticleVariant;
  prioritize?: boolean;
  style?: React.CSSProperties;
};

type VideoCardProps = VideoCardBaseProps & {
  video: Video;
};

export function VideoCard({
  video,
  variant = ArticleVariant.Default,
  showTag = true,
  showDate = true,
  style,
}: VideoCardProps) {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const { title, externalId, images, tags, data } = video;

  const publishDate = data?.snippet?.publishedAt;
  const publishDateFormatted = publishDate ? format(parseISO(publishDate), VIDEO_DATE_FORMAT) : '';
  const thumbnailUrl = images?.high?.url || images?.medium?.url || images?.default?.url;

  const className = cs('article-card', 'video-card', {
    'article-card--no-tag': !showTag,
    'article-card--no-date': !showDate,
    'article-card--default': variant === ArticleVariant.Default,
    'article-card--text-side-by-side': variant === ArticleVariant.TextSideBySide,
  });

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (externalId) {
        trackVideo.thumbnailClick(externalId, title);
      }
      setIsOverlayOpen(true);
    },
    [externalId, title]
  );

  const handleCloseOverlay = useCallback(() => {
    setIsOverlayOpen(false);
  }, []);

  return (
    <>
      <article style={style} className={className}>
        <a className="article-card__link-wrapper" href="#" onClick={handleClick}>
          {thumbnailUrl && (
            <div className="article-card__media-wrapper">
              <Image className="article-card__thumbnail" src={thumbnailUrl} alt={title} />
              <div className="video-card__play-button">
                <svg
                  className="video-card__play-icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          )}
          <div className="article-card__content">
            <div className="article-card__tag-date-wrapper">
              {tags.length > 0 && tags[0] && showTag && <span className="article-card__tag">{formatTag(tags[0])}</span>}
              {showDate && publishDate && (
                <time className="article-card__date" dateTime={publishDate}>
                  {publishDateFormatted}
                </time>
              )}
            </div>
            <TruncateMarkup lines={2}>
              <h2 className="article-card__title">{title}</h2>
            </TruncateMarkup>
          </div>
        </a>
      </article>
      {isOverlayOpen && externalId && (
        <VideoOverlay videoId={externalId} videoTitle={title} onClose={handleCloseOverlay} />
      )}
    </>
  );
}
